import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import type {
  GeneralSettings,
  GeneralSettingsRequest,
  SendTaxReportEmailRequest,
  SendTaxReportEmailResponse,
} from '../types'

export function useGeneralSettings(restaurantId: number) {
  const queryClient = useQueryClient()
  const queryKey = ['general-settings', restaurantId]

  // Fetch general settings
  const settingsQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await apiClient.get<GeneralSettings>(
        `/api/admin/restaurants/${restaurantId}/general`
      )
      return response.data
    },
    enabled: !!restaurantId,
  })

  // Update general settings
  const updateSettingsMutation = useMutation({
    mutationFn: async (data: GeneralSettingsRequest) => {
      await apiClient.put(
        `/api/admin/restaurants/${restaurantId}/general`,
        data
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
    },
  })

  // Upload logo
  const uploadLogoMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('logo', file)
      const response = await apiClient.post<{ path: string }>(
        `/api/admin/restaurants/${restaurantId}/logo`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      return response.data.path
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
    },
  })

  // Delete logo
  const deleteLogoMutation = useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/api/admin/restaurants/${restaurantId}/logo`)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
    },
  })

  // Upload icon
  const uploadIconMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('icon', file)
      const response = await apiClient.post<{ path: string }>(
        `/api/admin/restaurants/${restaurantId}/icon`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      return response.data.path
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
    },
  })

  // Delete icon
  const deleteIconMutation = useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/api/admin/restaurants/${restaurantId}/icon`)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
    },
  })

  // Upload discount category picture
  const uploadDiscountCategoryPictureMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('picture', file)
      const response = await apiClient.post<{ picturePath: string }>(
        `/api/admin/restaurants/${restaurantId}/discount-category-picture`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      return response.data.picturePath
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
    },
  })

  // Delete discount category picture
  const deleteDiscountCategoryPictureMutation = useMutation({
    mutationFn: async () => {
      await apiClient.delete(
        `/api/admin/restaurants/${restaurantId}/discount-category-picture`
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
    },
  })

  const sendTaxReportEmailMutation = useMutation({
    mutationFn: async (data: SendTaxReportEmailRequest) => {
      const response = await apiClient.post<SendTaxReportEmailResponse>(
        `/api/admin/restaurants/${restaurantId}/tax-report/email`,
        data
      )
      return response.data
    },
  })

  return {
    // Data
    settings: settingsQuery.data,

    // Loading states
    isLoading: settingsQuery.isLoading,
    isError: settingsQuery.isError,
    error: settingsQuery.error,
    refetch: settingsQuery.refetch,

    // Mutations
    updateSettings: updateSettingsMutation.mutateAsync,
    uploadLogo: uploadLogoMutation.mutateAsync,
    deleteLogo: deleteLogoMutation.mutateAsync,
    uploadIcon: uploadIconMutation.mutateAsync,
    deleteIcon: deleteIconMutation.mutateAsync,
    uploadDiscountCategoryPicture:
      uploadDiscountCategoryPictureMutation.mutateAsync,
    deleteDiscountCategoryPicture:
      deleteDiscountCategoryPictureMutation.mutateAsync,
    sendTaxReportEmail: sendTaxReportEmailMutation.mutateAsync,

    // Mutation states
    isUpdating:
      updateSettingsMutation.isPending ||
      uploadLogoMutation.isPending ||
      deleteLogoMutation.isPending ||
      uploadIconMutation.isPending ||
      deleteIconMutation.isPending ||
      uploadDiscountCategoryPictureMutation.isPending ||
      deleteDiscountCategoryPictureMutation.isPending ||
      sendTaxReportEmailMutation.isPending,
    isUploadingDiscountCategoryPicture:
      uploadDiscountCategoryPictureMutation.isPending ||
      deleteDiscountCategoryPictureMutation.isPending,
    isSendingTaxReportEmail: sendTaxReportEmailMutation.isPending,
  }
}
